class PasteForm extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.variantsData = JSON.parse(this.querySelector('[data-paste-variants-json]').textContent);
    this.variantsById = new Map(this.variantsData.map(variantData => [String(variantData.id), variantData]));
    this.variantsByProductId = new Map(this.variantsData.map(variantData => [String(variantData.productId), variantData]));
    this.packTypeHandle = this.dataset.packTypeHandle;
    this.isMainContext = this.dataset.pasteContext !== 'quick-shop';
    this.scope = this.closest('[data-paste-form-wrapper]') ?? this;
    this.totalPriceEl = this.closest('product-form-component')?.querySelector('[data-paste-total-price]');

    this.addEventListener('change', this.onInputChange.bind(this), { signal });
    this.initPasteModals(signal);

    // The quick shop of a paste PDP (the mobile sticky bar opens it) follows the pack and flavours chosen in the page form.
    // Its markup stays in the DOM once rendered, so the selections are taken over again every time the dialog opens.
    this.pageForm = this.isMainContext ? null : document.querySelector(`paste-form[data-paste-context="main"][data-product-id="${this.dataset.productId}"]`);
    if (this.pageForm) {
      this.closest('dialog')?.addEventListener('toggle', event => {
        if (event.newState === 'open') this.syncWithPageForm();
      }, { signal });
    }

    // Restore selections from a previous render of this form, then sync all UI to the current state
    this.restoreSelections();
    this.activePack = this.getActivePack();
    this.updateActiveSlots(parseInt(this.activePack, 10));
    this.updatePrice();
    this.updatePackImage();
    this.updatePackInfoContent();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onInputChange(event) {
    const name = event.target.name;
    if (!name) return;

    if (name === 'paste-pack') {
      this.carryOverSelections(this.activePack, event.target.value);
      this.activePack = event.target.value;
      this.updateActiveSlots(parseInt(event.target.value, 10));
    }

    this.updatePrice();
    this.updatePackImage();
    this.updatePackInfoContent();
    this.saveSelections();

    // The product media swiper returns to its first slide, the pack combination that just changed
    if (this.isMainContext) document.dispatchEvent(new CustomEvent('paste:selection-change'));
  }

  /**
   * Every pack size has its own set of slot radios. When the customer switches pack size,
   * the flavours chosen in the first slots carry over (4-pack -> 2-pack keeps the first two,
   * 2-pack -> 1-pack keeps the first one); slots beyond the previous pack size keep their own choice.
   * @param {string} fromPack previously active pack size
   * @param {string} toPack newly selected pack size
   */
  carryOverSelections(fromPack, toPack) {
    if (!fromPack || fromPack === toPack) return;

    const sharedSlotCount = Math.min(parseInt(fromPack, 10), parseInt(toPack, 10));

    for (let slotIndex = 1; slotIndex <= sharedSlotCount; slotIndex++) {
      const previousRadio = this.querySelector(`input[name="paste-slot-${fromPack}-pack-${slotIndex}"]:checked`);
      if (!previousRadio) continue;

      const targetRadio = this.querySelector(`input[name="paste-slot-${toPack}-pack-${slotIndex}"][value="${previousRadio.value}"]`);
      if (targetRadio) targetRadio.checked = true;
    }
  }

  updateActiveSlots(packSize) {
    if (!this.isMainContext) return;

    const featuredImage = document.querySelector('[data-pastes-featured-image]');
    if (featuredImage) {
      featuredImage.style.gridTemplateColumns = `repeat(${packSize}, 1fr)`;
      featuredImage.querySelectorAll('.slot').forEach((slot, index) => {
        slot.hidden = index >= packSize;
      });
    }
  }

  getActivePack() {
    const checked = this.querySelector('input[name="paste-pack"]:checked');
    return checked ? checked.value : '1';
  }

  getSelectedVariantIds() {
    const pack = this.getActivePack();
    const count = parseInt(pack, 10);
    const ids = [];

    for (let i = 1; i <= count; i++) {
      const radio = this.querySelector(`input[name="paste-slot-${pack}-pack-${i}"]:checked`);
      if (radio) ids.push(radio.value);
    }

    return ids;
  }

  updatePrice() {
    let activePackPriceCents = null;

    // Flavours have their own prices: every pack option shows the sum of its selected flavours minus the pack discount
    this.querySelectorAll('.paste-pack-option').forEach(packOptionEl => {
      const packInput = packOptionEl.querySelector('input[name="paste-pack"]');
      const packPriceEl = packOptionEl.querySelector('[data-paste-pack-price]');
      const selectedVariants = [...packOptionEl.querySelectorAll('.paste-pack-slots input:checked')].map(radio => this.variantsById.get(radio.value));
      if (!packInput || !packPriceEl || !selectedVariants.length || selectedVariants.includes(undefined)) return;

      const packPrice = selectedVariants.reduce((sum, variantData) => sum + variantData.price, 0);
      const packDiscount = (parseInt(packInput.dataset.packDiscountPerItem, 10) || 0) * selectedVariants.length;

      const compareAtPriceEl = packOptionEl.querySelector('[data-paste-pack-compare-price]');
      if (compareAtPriceEl) compareAtPriceEl.textContent = formatMoneyShort(packPrice);
      packPriceEl.textContent = formatMoneyShort(packPrice - packDiscount);
      if (packInput.checked) activePackPriceCents = packPrice - packDiscount;
    });

    const activePackInput = this.querySelector('input[name="paste-pack"]:checked');
    if (!activePackInput) return;

    const packOptionEl = activePackInput.closest('.paste-pack-option');
    if (!packOptionEl) return;

    const priceEl = packOptionEl.querySelector('[data-paste-pack-price]');
    if (!priceEl || !this.totalPriceEl) return;

    // Upsell products checked in the product form (snippets/product-upsell.liquid) count towards the add to cart price
    const upsellInputs = this.closest('product-form-component')?.querySelectorAll('input.product-form__upsell-input:checked') ?? [];
    const upsellPriceCents = [...upsellInputs].reduce((sum, upsellInput) => sum + (parseInt(upsellInput.dataset.upsellPrice, 10) || 0), 0);

    if (activePackPriceCents !== null && upsellPriceCents > 0) {
      this.totalPriceEl.textContent = formatMoneyShort(activePackPriceCents + upsellPriceCents);
    }
    else {
      this.totalPriceEl.textContent = priceEl.textContent;
    }
  }

  updatePackInfoContent() {
    if (!this.isMainContext) return;

    const container = document.querySelector('[data-pack-info-content]');
    const template = document.getElementById('PackInfoContent');
    if (!container || !template) return;

    const ids = this.getSelectedVariantIds();
    const seen = new Set();
    const fragments = [];

    for (const id of ids) {
      const variant = this.variantsById.get(String(id));
      if (!variant || !variant.description) continue;
      if (seen.has(variant.productId)) continue;
      seen.add(variant.productId);

      const clone = template.content.cloneNode(true);
      clone.querySelector('summary span').textContent = variant.title;
      clone.querySelector('.rte').innerHTML = variant.description;
      fragments.push(clone);
    }

    container.replaceChildren(...fragments);
  }

  updatePackImage() {
    const ids = this.getSelectedVariantIds();

    const display = this.scope.querySelector('[data-paste-image-display]');
    if (display) {
      const images = ids
        .map(id => {
          const variant = this.variantsById.get(String(id));
          if (!variant) return null;
          const image = document.createElement('img');
          image.src = variant.image;
          image.alt = variant.title;
          image.loading = 'lazy';
          image.width = 400;
          image.height = 400;
          return image;
        })
        .filter(Boolean);
      display.replaceChildren(...images);
    }

    if (!this.isMainContext) return;

    const featuredImage = document.querySelector('[data-pastes-featured-image]');
    if (featuredImage) {
      const slots = featuredImage.querySelectorAll('.slot');
      ids.forEach((id, index) => {
        const variant = this.variantsById.get(String(id));
        const slot = slots[index];
        if (!slot || !variant) return;
        const img = slot.querySelector('img');
        if (img) {
          img.src = variant.image;
          img.alt = variant.title;
          img.removeAttribute('srcset');
        }
      });
    }
  }

  get storageKey() {
    return `paste-selections:${this.packTypeHandle}:${this.dataset.productId}:${this.dataset.pasteContext}`;
  }

  saveSelections() {
    const pack = this.getActivePack();
    const count = parseInt(pack, 10);
    const selections = {};
    for (let i = 1; i <= count; i++) {
      const radio = this.querySelector(`input[name="paste-slot-${pack}-pack-${i}"]:checked`);
      if (radio) selections[`paste-slot-${pack}-pack-${i}`] = radio.value;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ pack, selections }));
    }
    catch {/* localStorage unavailable */}
  }

  restoreSelections() {
    if (this.pageForm) return this.copySelectionsFrom(this.pageForm);

    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(this.storageKey));
    }
    catch {
      return false;
    }
    if (!stored) return false;

    const packRadio = this.querySelector(`input[name="paste-pack"][value="${stored.pack}"]`);
    if (packRadio) packRadio.checked = true;

    for (const [slotName, variantId] of Object.entries(stored.selections)) {
      const radio = this.querySelector(`input[name="${slotName}"][value="${variantId}"]`);
      if (radio) radio.checked = true;
    }

    return true;
  }

  syncWithPageForm() {
    this.copySelectionsFrom(this.pageForm);
    this.activePack = this.getActivePack();
    this.updatePrice();
    this.updatePackImage();
  }

  /**
   * Checks the same pack size and flavour radios as in another paste form of the same product.
   * The radio names are the same in both forms, only the ids carry the context suffix (snippets/paste-slot.liquid).
   * @param {PasteForm} sourceForm the form to copy the selections from
   */
  copySelectionsFrom(sourceForm) {
    const packRadio = this.querySelector(`input[name="paste-pack"][value="${sourceForm.getActivePack()}"]`);
    if (packRadio) packRadio.checked = true;

    sourceForm.querySelectorAll('.paste-pack-slots input:checked').forEach(sourceRadio => {
      const radio = this.querySelector(`input[name="${sourceRadio.name}"][value="${sourceRadio.value}"]`);
      if (radio) radio.checked = true;
    });

    return true;
  }

  initPasteModals(signal) {
    const selectModal = this.scope.querySelector('[data-paste-select-modal]');
    const infoModal = this.scope.querySelector('[data-paste-info-modal]');

    if (!selectModal && !infoModal) return;

    // Capture which slot triggered the select modal
    this.scope.addEventListener('click', event => {
      const wijzigBtn = event.target.closest('[data-paste-wijzig]');
      if (!wijzigBtn || !selectModal) return;

      selectModal.dataset.currentSlot = wijzigBtn.dataset.slotName;

      // Polyfill: open modal for browsers without native Invoker Commands support
      if (!selectModal.open) {
        selectModal.showModal();
      }

      // Highlight the currently selected option in the modal
      const currentRadio = this.querySelector(`input[name="${wijzigBtn.dataset.slotName}"]:checked`);
      const currentVariantId = currentRadio?.value;

      selectModal.querySelectorAll('[data-paste-select-option]').forEach(option => {
        option.classList.toggle('is-selected', option.dataset.variantId === currentVariantId);
      });
    }, { signal });

    // On flavor option click: check the right radio and close modal
    selectModal?.addEventListener('click', event => {
      const option = event.target.closest('[data-paste-select-option]');
      if (!option) return;

      const slotName = selectModal.dataset.currentSlot;
      const variantId = option.dataset.variantId;
      const radio = this.querySelector(`input[name="${slotName}"][value="${variantId}"]`);

      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      selectModal.close();
    }, { signal });

    // Capture which variant triggered the info modal
    this.scope.addEventListener('click', event => {
      const infoBtn = event.target.closest('[data-paste-info]');
      if (!infoBtn || !infoModal) return;

      const variantData = this.variantsByProductId.get(String(infoBtn.dataset.productId));
      if (!variantData) return;

      const template = this.scope.querySelector('[data-paste-info-template]');
      const clone = template.content.cloneNode(true);

      const link = clone.querySelector('[data-paste-info-link]');
      link.href = variantData.url;

      // Carousel of the pack photos; without pack photos the single product image is shown
      const hasPackImages = variantData.packImages.length > 0;
      const images = hasPackImages ? variantData.packImages : [ { src: variantData.image, alt: variantData.title } ];

      const track = clone.querySelector('[data-paste-info-track]');
      const slideTemplate = track.querySelector('[data-paste-info-slide]');
      const slides = images.map(image => {
        const slide = slideTemplate.cloneNode(true);
        const img = slide.querySelector('[data-paste-info-image]');
        img.src = image.src;
        img.alt = image.alt || variantData.title;
        return slide;
      });
      track.replaceChildren(...slides);

      if (images.length < 2) {
        clone.querySelectorAll('[data-paste-info-prev], [data-paste-info-next]').forEach(button => button.remove());
      }

      const imageEl = clone.querySelector('.paste-info-variant__image');
      imageEl.classList.toggle('paste-info-carousel--packshot', !hasPackImages);
      // Product colors for the .product-image background
      if (variantData.mainColor) imageEl.style.setProperty('--product-color-main', variantData.mainColor);
      if (variantData.accentColor) imageEl.style.setProperty('--product-color-accent', variantData.accentColor);

      clone.querySelector('[data-paste-info-name]').textContent = variantData.title;

      const descriptionEl = clone.querySelector('[data-paste-info-description]');
      if (variantData.description) {
        descriptionEl.innerHTML = variantData.description;
      }
      else {
        descriptionEl.remove();
      }

      const content = infoModal.querySelector('[data-paste-info-content]');
      content.replaceChildren(clone);

      // Polyfill: open modal for browsers without native Invoker Commands support
      if (!infoModal.open) {
        infoModal.showModal();
      }
    }, { signal });

    // Carousel arrows: scroll the track one photo back or forward
    infoModal?.addEventListener('click', event => {
      const arrow = event.target.closest('[data-paste-info-prev], [data-paste-info-next]');
      if (!arrow) return;

      const track = infoModal.querySelector('[data-paste-info-track]');
      const direction = arrow.matches('[data-paste-info-next]') ? 1 : -1;
      track.scrollBy({ left: direction * track.clientWidth });
    }, { signal });

    // Disable the arrows at the first and last photo. Scroll events do not bubble, so listen in the capture phase.
    infoModal?.addEventListener('scroll', event => {
      const track = event.target;
      if (track.nodeType !== Node.ELEMENT_NODE || !track.matches('[data-paste-info-track]')) return;

      const prevArrow = infoModal.querySelector('[data-paste-info-prev]');
      const nextArrow = infoModal.querySelector('[data-paste-info-next]');
      if (!prevArrow || !nextArrow) return;

      prevArrow.disabled = track.scrollLeft <= 1;
      nextArrow.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }, { capture: true, passive: true, signal });

    // Mouse drag on the photos. Touch and trackpad scroll the track natively, a mouse cannot.
    let drag = null;

    infoModal?.addEventListener('pointerdown', event => {
      const track = event.target.closest('[data-paste-info-track]');
      if (!track || event.pointerType !== 'mouse' || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth) return;

      // Capture the pointer so the drag also ends when the mouse is released outside the dialog
      track.setPointerCapture(event.pointerId);
      drag = { track, startX: event.clientX, startScrollLeft: track.scrollLeft };
    }, { signal });

    infoModal?.addEventListener('pointermove', event => {
      if (!drag) return;

      // data-dragging switches off snapping and smooth scrolling (see the stylesheet), so the photo follows the mouse
      drag.track.dataset.dragging = '';
      drag.track.scrollLeft = drag.startScrollLeft - (event.clientX - drag.startX);
    }, { signal });

    const endDrag = event => {
      if (!drag) return;

      const { track, startX, startScrollLeft } = drag;
      drag = null;

      // A drag of more than 40px goes to the previous or next photo, a shorter one returns to the current photo
      const distance = event.clientX - startX;
      const startIndex = Math.round(startScrollLeft / track.clientWidth);
      const targetIndex = Math.abs(distance) > 40 ? startIndex - Math.sign(distance) : startIndex;

      delete track.dataset.dragging;
      track.scrollTo({ left: targetIndex * track.clientWidth });
    };

    infoModal?.addEventListener('pointerup', endDrag, { signal });
    infoModal?.addEventListener('pointercancel', endDrag, { signal });
  }
}

if (!customElements.get('paste-form')) {
  customElements.define('paste-form', PasteForm);
}
